import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, UserPlus, Trash2, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";

export default function Waitlist() {
  const [entries, setEntries] = useState(null);
  const load = () => api.get("/admin/waitlist").then((r) => setEntries(r.data)).catch(() => setEntries([]));
  useEffect(() => { load(); }, []);

  const promote = async (w) => {
    if (!window.confirm(`Email ${w.email} that a spot opened up for ${w.park_name}?`)) return;
    try { await api.post(`/admin/waitlist/${w.id}/promote`); toast.success("Customer emailed — spot offered"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Remove ${w.email} from the waitlist?`)) return;
    try { await api.delete(`/admin/waitlist/${w.id}`); toast.success("Removed from waitlist"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const waiting = (entries || []).filter((e) => e.status === "waiting");
  const promoted = (entries || []).filter((e) => e.status === "promoted");

  return (
    <div data-testid="waitlist-page">
      <h1 className="font-display text-4xl">Waitlist</h1>
      <p className="text-brand-ink/60 dark:text-white/50 mt-2">When a class is full, customers join here. Promote someone to auto-email them a booking link.</p>

      <div className={`${card} mt-8 overflow-hidden`}>
        <div className="px-6 py-4 border-b border-brand-line dark:border-neutral-800 font-display text-xl flex items-center gap-2"><ListChecks className="w-5 h-5 text-brand-red" /> Waiting ({waiting.length})</div>
        {!entries ? <div className="p-6 text-brand-ink/50 dark:text-white/40">Loading…</div> : waiting.length === 0 ? (
          <div className="p-6 text-brand-ink/50 dark:text-white/40" data-testid="waitlist-empty">No one is waiting right now.</div>
        ) : (
          <div className="divide-y divide-brand-line dark:divide-neutral-800">
            {waiting.map((w) => (
              <div key={w.id} className="px-6 py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`waitlist-${w.id.slice(0, 8)}`}>
                <div>
                  <div className="font-medium">{w.name} <span className="text-brand-ink/40 dark:text-white/40 text-sm">· {w.email}</span></div>
                  <div className="text-sm text-brand-ink/50 dark:text-white/40 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {w.park_name} · {w.date} {w.time}{w.phone ? ` · ${w.phone}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => promote(w)} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-brand-red text-white hover:bg-brand-reddark transition-colors" data-testid={`promote-${w.id.slice(0, 8)}`}><UserPlus className="w-4 h-4" /> Promote</button>
                  <button onClick={() => remove(w)} className="inline-flex items-center gap-1 text-sm text-brand-ink/50 dark:text-white/40 hover:text-brand-red transition-colors" data-testid={`remove-${w.id.slice(0, 8)}`}><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {promoted.length > 0 && (
        <div className={`${card} mt-6 overflow-hidden`}>
          <div className="px-6 py-4 border-b border-brand-line dark:border-neutral-800 font-display text-xl">Promoted ({promoted.length})</div>
          <div className="divide-y divide-brand-line dark:divide-neutral-800">
            {promoted.map((w) => (
              <div key={w.id} className="px-6 py-4 flex items-center justify-between gap-3">
                <div className="font-medium">{w.name} <span className="text-brand-ink/40 dark:text-white/40 text-sm">· {w.email}</span></div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Spot offered</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
